import RegionRankedSelect from "@core-components/regionSelector/select/RegionRankedSelect";
import SSHeadline from "@core-components/title/SSHeadline";
import SSLoLGradientText from "@core-components/title/components/SSLoLGradientText";
import SSWhiteText from "@core-components/title/components/SSWhiteText";
import type { AppRegion } from "@core-types/region.enum";
import useAppTranslation from "@hooks/language/useAppTranslation";
import { Group } from "@mantine/core";
import { Trans } from "react-i18next";
import Emoji from "../../shared/emoji/Emoji";
import FilterChip from "../../shared/filter-chip/FilterChip";
import PriceSelect from "../components/PriceSelect";
import RankSelect from "../components/RankSelect";
import SkinSelect from "../components/SkinSelect";
import type { ISkinOption } from "../hooks/useSmurfsRankedFilters";

interface SmurfsRankedFilterSectionProps {
  region: AppRegion;
  handleRegionChange: (region: AppRegion) => void;
  filters: {
    ranks: string[];
    price: string[];
    skins: string[];
  };
  skinsList: ISkinOption[];
  setters: {
    setRanksFilters: (ranks: string[]) => void;
    setPriceRangesFilters: (ranges: string[]) => void;
    setSkinsFilters: (skins: string[]) => void;
  };
}

export const SmurfsRankedFilterSection = ({
  region,
  handleRegionChange,
  filters,
  setters,
  skinsList,
}: SmurfsRankedFilterSectionProps) => {
  const { t } = useAppTranslation("pages.ranked");

  return (
    <>
      <SSHeadline
        props={{
          stack: {
            align: "start",
            className: "w-full px-3 py-0 xs:px-10 xs:py-5",
          },
        }}
      >
        <Emoji.Trophy size={24} />
        <Trans t={t} i18nKey={"list.title"}>
          <SSWhiteText> NOS COMPTE </SSWhiteText>
          <SSLoLGradientText>CLASSÉS</SSLoLGradientText>
          <SSWhiteText> BALISE POUR ANGLAIS </SSWhiteText>
        </Trans>
      </SSHeadline>
      <Group justify="center">
        <RegionRankedSelect value={region} onChange={handleRegionChange} />
        <RankSelect values={filters.ranks} onChange={setters.setRanksFilters} />
        <PriceSelect
          values={filters.price}
          onChange={setters.setPriceRangesFilters}
        />
        <SkinSelect
          values={filters.skins}
          onChange={setters.setSkinsFilters}
          skins={skinsList}
        />
      </Group>

      {(filters.ranks.length > 0 ||
        filters.price.length > 0 ||
        filters.skins.length > 0) && (
        <Group gap="sm" justify="center" mt="md">
          {filters.ranks.map((rank) => (
            <FilterChip
              key={rank}
              label={t("filters.rank")}
              onRemove={() =>
                setters.setRanksFilters(filters.ranks.filter((r) => r !== rank))
              }
              value={rank}
            />
          ))}
          {filters.price.map((p) => (
            <FilterChip
              key={p}
              label={t("filters.price")}
              onRemove={() =>
                setters.setPriceRangesFilters(
                  filters.price.filter((pr) => pr !== p),
                )
              }
              value={p}
            />
          ))}
          {filters.skins.map((skinId) => {
            const skinName =
              skinsList.find((s) => s.id.toString() === skinId)?.name || skinId;
            return (
              <FilterChip
                key={skinId}
                label={t("filters.skin")}
                onRemove={() =>
                  setters.setSkinsFilters(
                    filters.skins.filter((s) => s !== skinId),
                  )
                }
                value={skinName}
              />
            );
          })}
        </Group>
      )}
    </>
  );
};
